// Client boot: createState -> ui.initUI (returns getName) -> connectNet ->
// initInput -> startRenderer, plus canvas sizing and the visible-chunk
// auto-request loop.

import { MAX_CHUNK_REQ } from '../shared/constants'
import { BUILD_ID } from '../shared/version'
import { chunkOf, chunkKey, hexToPixel } from '../shared/hex'
import type { ClientMsg } from '../shared/protocol'
import { initInput } from './input'
import { resetCamBounds } from './render/fog'
import { connectNet } from './net'
import { fogOf } from './render/fog'
import { startRenderer, visibleChunkKeys } from './render/renderer'
import { startRenderer3d } from './render3d'
import { createState, cullGhosts, HEX_SIZE , use3dView } from './state'
import { initUI } from './ui/hud'

const CHUNK_POLL_MS = 500
/** Don't re-request a chunk we already asked for within this window. */
const CHUNK_RETRY_MS = 4000

const canvas = document.getElementById('game') as HTMLCanvasElement

// Canvas fills the viewport (CSS size; the renderer handles DPR bitmap sizing).
function fitCanvas(): void {
  canvas.style.display = 'block'
  canvas.style.width = window.innerWidth + 'px'
  canvas.style.height = window.innerHeight + 'px'
}
window.addEventListener('resize', fitCanvas)
fitCanvas()

const st = createState()

// UI gets a ref object so it can send before the socket exists.
const sendRef: { current: ((m: ClientMsg) => void) | null } = { current: null }
const galleryMode = new URLSearchParams(location.search).has('gallery')
const ui = galleryMode ? null : initUI(st, sendRef)

// Center the camera on home whenever we (re)join — and forget the previous
// realm's camera bounds first, or the clamp yanks the view back toward the
// OLD village (restart/respawn found elsewhere → player stares at empty fog).
st.on('welcome', () => {
  resetCamBounds()
  if (!st.home) return
  const p = hexToPixel(st.home.q, st.home.r, HEX_SIZE)
  st.cam.x = p.x
  st.cam.y = p.y
})

// Every send routes through the ref, so input/render are wired before the
// socket exists (and survive reconnects).
const send = (m: ClientMsg): void => sendRef.current?.(m)

// Renderer selection: the WebGL 3D view is the DEFAULT. The classic 2.5D canvas
// renderer remains available via the settings toggle (persisted as hexClassic) or
// ?classic; ?r3d still forces 3D. The 3D module binds its own camera/input, so
// initInput (2.5D picking/commands) is only wired for the 2D path.
const use3d = galleryMode || use3dView()
if (use3d) {
  startRenderer3d(canvas, st, send)
} else {
  initInput(canvas, st, send)
  startRenderer(canvas, st, send)
}

// Debug/testing handles (also handy in devtools).
;(window as unknown as Record<string, unknown>).HEX = st
;(window as unknown as Record<string, unknown>).HEXSEND = send

// Sign-in gate: connect the game socket only once a session exists; otherwise
// show the login modal. After a provider round-trip the page reloads here
// already authenticated and connects automatically.
void (async () => {
  if (galleryMode || !ui) return // offline art viewer; never opens a game session
  // Admin observe mode bypasses the sign-in gate entirely: the ws itself
  // carries the admin token and the server ignores any account cookie.
  if (new URLSearchParams(location.search).has('observe')) {
    const net = connectNet(st)
    sendRef.current = (m) => net.send(m)
    return
  }
  let authed = false
  let provider: string | undefined
  let needsEmail = false
  try {
    const r = await fetch('/auth/me', { credentials: 'same-origin' })
    const body = (await r.json()) as { authed?: boolean; provider?: string; needsEmail?: boolean }
    authed = r.ok && Boolean(body.authed)
    provider = body.provider
    needsEmail = body.needsEmail === true
  } catch {
    authed = false
  }
  if (!authed) {
    ui.showLogin()
    return
  }
  ui.setAccount(provider)
  // Email backfill: accounts from before email capture bounce through a silent
  // Google re-auth (prompt=none — no UI) at most once a day until it lands.
  try {
    // Attempts reset per DAY and per BUILD — a new deploy means new fixes,
    // so accounts still missing an email get a fresh shot immediately.
    const rawTries = localStorage.getItem('hexEmailRefresh') ?? ''
    const [dayStr, buildStr, nStr] = rawTries.split('|')
    const today = Math.floor(Date.now() / 86_400_000)
    const tries = Number(dayStr) === today && buildStr === BUILD_ID ? Number(nStr ?? 0) : 0
    if (needsEmail && tries < 2) {
      localStorage.setItem('hexEmailRefresh', `${today}|${BUILD_ID}|${tries + 1}`)
      // First try is invisible (prompt=none). Google testing-mode grants
      // expire after 7 days and make silent fail — so the second try uses the
      // normal one-click account chooser, which always lands the email.
      location.href = tries === 0 ? '/auth/google?silent=1' : '/auth/google'
      return
    }
  } catch { /* private mode */ }
  const net = connectNet(st)
  sendRef.current = (m) => net.send(m)
})()

// Every 500ms: refresh the server-side subscription to exactly our currently
// visible chunks (cheap, no payloads — this also self-heals after an LRU
// eviction), then separately request data only for the visible chunks we
// haven't loaded yet (batched, capped).
const requested = new Map<string, { at: number; delay: number }>() // chunkKey -> retry backoff
const lastVis = new Map<string, boolean>() // loaded chunkKey -> was it in vision last poll
setInterval(() => {
  if (!st.connected || (!st.me && !st.observer)) return // wait for welcome before requesting chunks
  const now = Date.now()
  const wanted = new Set(visibleChunkKeys(st, canvas))
  // Offscreen explorers still reveal terrain on the minimap. Their ordinary
  // sight fits within the neighboring chunks, which use the same server fog
  // checks and capped request batches as the camera's terrain.
  for (const u of st.units.values()) {
    if (u.owner !== st.me || !u.exploring || u.hp <= 0) continue
    const at = chunkOf(u.q, u.r)
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      wanted.add(chunkKey(at.cx + dx, at.cy + dy))
    }
  }
  const visible = [...wanted]
  const fog = fogOf(st)
  // Subscription refresh: our sub set should equal our visible set.
  send({ t: 'view', keys: visible })
  // Drop ghosts: entities that left the loaded map with no removal message.
  cullGhosts(st)
  const want: string[] = []
  for (const key of visible) {
    if (want.length >= MAX_CHUNK_REQ) break
    const inVision = !fog || fog.chunks.has(key)
    if (st.chunks.has(key)) {
      requested.delete(key)
      // Fog memory went stale while out of sight: the server suppressed its
      // tile diffs, so re-request the chunk the moment vision returns.
      if (inVision && lastVis.get(key) === false) want.push(key)
      lastVis.set(key, inVision)
      continue
    }
    // Unloaded: fetch with exponential backoff — unexplored chunks are
    // (correctly) never served, so don't hammer the server for them forever.
    const ent = requested.get(key)
    if (ent && now - ent.at < ent.delay) continue
    want.push(key)
    requested.set(key, { at: now, delay: ent ? Math.min(ent.delay * 2, 60_000) : CHUNK_RETRY_MS })
  }
  if (want.length) send({ t: 'chunks', keys: want })
}, CHUNK_POLL_MS)
